import React, { useEffect, useState } from "react";
import { X, Bot, Plus, Pencil, Trash2, ArrowLeft, Sparkles, Loader2, Check, Paperclip, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAgents } from "../lib/agents-context";
import { app as appApi } from "../lib/api";

function formatBytes(n) {
    if (!n || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * AgentsDialog — gerenciador completo de agentes personalizados.
 * Duas views: "list" (Meus Agentes) e "form" (criar/editar).
 */
export default function AgentsDialog() {
    const { dialog, closeAgentsDialog, agents, createAgent, updateAgent, deleteAgent, selectedAgentId, setSelectedAgentId } = useAgents();
    const { open } = dialog;

    const [view, setView] = useState("list");
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Animação entrada/saída
    const [render, setRender] = useState(open);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (open) {
            setView(dialog.view || "list");
            setEditing(dialog.editing || null);
            setConfirmDelete(null);
            setRender(true);
            const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
            return () => cancelAnimationFrame(raf);
        }
        setShow(false);
        const t = setTimeout(() => setRender(false), 250);
        return () => clearTimeout(t);
    }, [open, dialog]);

    if (!render) return null;

    const goForm = (agent = null) => { setEditing(agent); setView("form"); };
    const goList = () => { setEditing(null); setView("list"); };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6" data-testid="agents-dialog" role="dialog" aria-modal="true">
            <div
                className={`absolute inset-0 bg-black/80 backdrop-blur-sm ${show ? "opacity-100" : "opacity-0"}`}
                style={{ transitionProperty: "opacity", transitionDuration: "250ms" }}
                onClick={closeAgentsDialog}
            />
            <div
                className={`relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-[#0d0d0d] border border-border rounded-lg shadow-2xl ${show ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"}`}
                style={{ transitionProperty: "opacity, transform", transitionDuration: "250ms", transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
            >
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-border bg-[#0d0d0d]">
                    <div className="flex items-center gap-3">
                        {view === "form" && (
                            <button type="button" onClick={goList} data-testid="agents-back-btn"
                                className="p-1.5 -ml-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors" aria-label="Voltar">
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                        )}
                        <div>
                            <h2 className="font-display text-base font-bold tracking-tight">
                                {view === "form" ? (editing ? "Editar Agente" : "Criar Agente") : "Meus Agentes"}
                            </h2>
                            {view === "list" && (
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                    Crie especialistas para conduzir suas conversas.
                                </p>
                            )}
                        </div>
                    </div>
                    <button type="button" onClick={closeAgentsDialog} data-testid="agents-dialog-close"
                        className="p-2 -mr-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors shrink-0" aria-label="Fechar">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {view === "list" ? (
                    <AgentsList
                        agents={agents}
                        selectedAgentId={selectedAgentId}
                        onUse={(a) => { setSelectedAgentId(a.id); closeAgentsDialog(); }}
                        onEdit={(a) => goForm(a)}
                        onCreate={() => goForm(null)}
                        confirmDelete={confirmDelete}
                        setConfirmDelete={setConfirmDelete}
                        onDelete={async (a) => {
                            try { await deleteAgent(a.id); setConfirmDelete(null); toast.success("Agente excluído."); }
                            catch { toast.error("Erro ao excluir agente."); }
                        }}
                    />
                ) : (
                    <AgentForm
                        agent={editing}
                        onCancel={goList}
                        onSave={async (payload) => {
                            try {
                                if (editing) {
                                    await updateAgent(editing.id, payload);
                                } else {
                                    const a = await createAgent(payload);
                                    setSelectedAgentId(a.id);
                                }
                                goList();
                            } catch (err) {
                                toast.error(err?.response?.data?.detail || "Erro ao salvar agente.");
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
}

function AgentsList({ agents, selectedAgentId, onUse, onEdit, onCreate, onDelete, confirmDelete, setConfirmDelete }) {
    return (
        <div className="p-5 space-y-3">
            <button type="button" onClick={onCreate} data-testid="agents-create-btn"
                className="w-full py-3 rounded-md border border-dashed border-primary/40 text-primary hover:bg-primary/10 transition-colors text-sm font-semibold flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Criar novo agente
            </button>

            {agents.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
                    <p className="text-sm">Você ainda não tem agentes.</p>
                    <p className="text-xs mt-1">Crie um especialista: redator, programador, consultor...</p>
                </div>
            )}

            {agents.map((a) => {
                const active = a.id === selectedAgentId;
                const deleting = confirmDelete === a.id;
                return (
                    <div key={a.id} data-testid={`agent-card-${a.id}`}
                        className={`border rounded-md p-4 transition-colors ${active ? "border-primary bg-primary/[0.06]" : "border-border bg-[#0a0a0a]"}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-full border border-border overflow-hidden bg-[#111] flex items-center justify-center shrink-0">
                                    {a.avatar_url ? (
                                        <img src={a.avatar_url} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/img/default-avatar.svg"; }} />
                                    ) : (
                                        <Bot className="w-4.5 h-4.5 text-muted-foreground" strokeWidth={2} />
                                    )}
                                </div>
                                <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm truncate">{a.name}</span>
                                    {active && (
                                        <span className="bg-primary text-white text-[8px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm shrink-0">Ativo</span>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description}</p>
                                {a.skills?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {a.skills.slice(0, 6).map((s, i) => (
                                            <span key={i} className="text-[10px] font-mono bg-muted/50 border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground max-w-[160px] truncate">{s}</span>
                                        ))}
                                        {a.skills.length > 6 && <span className="text-[10px] text-muted-foreground">+{a.skills.length - 6}</span>}
                                    </div>
                                )}
                                {(a.knowledge_files?.length > 0 || a.description_url) && (
                                    <div className="flex items-center gap-1.5 mt-2 text-[10px] font-mono text-primary/80" data-testid={`agent-kb-badge-${a.id}`}>
                                        <Paperclip className="w-3 h-3" />
                                        Base de conhecimento: {(a.knowledge_files?.length || 0) + (a.description_url ? 1 : 0)} arquivo(s)
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button type="button" onClick={() => onEdit(a)} data-testid={`agent-edit-${a.id}`}
                                    className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" aria-label="Editar agente">
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => setConfirmDelete(deleting ? null : a.id)} data-testid={`agent-delete-${a.id}`}
                                    className="p-1.5 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Excluir agente">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                        {deleting ? (
                            <div className="flex items-center justify-between gap-2 mt-3 border border-red-500/30 bg-red-500/10 rounded-sm px-3 py-2">
                                <span className="text-xs text-red-300">Excluir "{a.name}"?</span>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setConfirmDelete(null)} className="text-xs text-muted-foreground hover:text-foreground">Cancelar</button>
                                    <button type="button" onClick={() => onDelete(a)} data-testid={`agent-delete-confirm-${a.id}`}
                                        className="text-xs font-semibold text-red-400 hover:text-red-300">Excluir</button>
                                </div>
                            </div>
                        ) : (
                            !active && (
                                <button type="button" onClick={() => onUse(a)} data-testid={`agent-use-${a.id}`}
                                    className="mt-3 w-full py-1.5 rounded-sm border border-border text-xs font-semibold text-foreground hover:border-primary/50 hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5">
                                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                    Usar este agente
                                </button>
                            )
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function AgentForm({ agent, onSave, onCancel }) {
    // Inputs não-controlados (refs) — colar textos gigantes não trava a UI
    const nameRef = React.useRef(null);
    const descRef = React.useRef(null);
    const skillRef = React.useRef(null);
    const counterRef = React.useRef(null);
    const [loadingDesc, setLoadingDesc] = useState(false);
    const [skills, setSkills] = useState(agent?.skills || []);
    const [knowledgeFiles, setKnowledgeFiles] = useState(agent?.knowledge_files || []);
    const [avatar, setAvatar] = useState({ url: agent?.avatar_url || "", key: agent?.avatar_key || "" });
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = React.useRef(null);
    const avatarFileRef = React.useRef(null);

    const handleAvatar = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) { toast.error("A foto deve ser uma imagem."); return; }
        if (f.size > 10 * 1024 * 1024) { toast.error("A imagem não pode passar de 10MB."); return; }
        setAvatarUploading(true);
        try {
            const res = await appApi.upload([f], "agents");
            const up = res?.uploaded?.[0];
            if (up) setAvatar({ url: up.url, key: up.key });
            else toast.error("Falha no upload da foto.");
        } catch {
            toast.error("Falha no upload da foto.");
        } finally {
            setAvatarUploading(false);
        }
    };

    const updateCounter = () => {
        if (counterRef.current && descRef.current) {
            counterRef.current.textContent = `${descRef.current.value.length.toLocaleString("pt-BR")} caracteres`;
        }
    };

    // Descrição longa mora num .md no R2 — busca o texto completo p/ edição
    useEffect(() => {
        if (!agent?.description_url) return;
        setLoadingDesc(true);
        fetch(agent.description_url)
            .then((r) => (r.ok ? r.text() : Promise.reject()))
            .then((txt) => {
                if (descRef.current) { descRef.current.value = txt; updateCounter(); }
            })
            .catch(() => toast.error("Não foi possível carregar a descrição completa. Editando o trecho salvo."))
            .finally(() => setLoadingDesc(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent]);

    const addSkill = () => {
        const s = (skillRef.current?.value || "").trim();
        if (!s) return;
        if (skills.length >= 100) { toast.error("Máximo de 100 habilidades."); return; }
        if (skills.some((x) => x.toLowerCase() === s.toLowerCase())) { skillRef.current.value = ""; return; }
        setSkills((prev) => [...prev, s]);
        skillRef.current.value = "";
    };

    const handleFiles = async (e) => {
        const list = Array.from(e.target.files || []);
        e.target.value = "";
        if (!list.length) return;
        setUploading(true);
        try {
            const res = await appApi.upload(list, "agents");
            const ok = res?.uploaded || [];
            if (ok.length > 0) {
                setKnowledgeFiles((prev) => [...prev, ...ok.map((f) => ({
                    filename: f.filename, url: f.url, key: f.key,
                    content_type: f.content_type, size: f.size,
                }))]);
            }
            for (const f of res?.failures || []) toast.error(`${f.filename}: ${f.error}`);
        } catch {
            toast.error("Falha no upload dos arquivos.");
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        const name = (nameRef.current?.value || "").trim();
        const description = (descRef.current?.value || "").trim();
        if (!name) { toast.error("Dê um nome ao agente."); return; }
        if (!description) { toast.error("Descreva o que o agente faz."); return; }
        setSaving(true);
        await onSave({ name, description, skills, knowledge_files: knowledgeFiles, avatar_url: avatar.url, avatar_key: avatar.key });
        setSaving(false);
    };

    return (
        <form onSubmit={submit} className="p-5 space-y-4">
            {/* Foto do agente */}
            <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                    <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} data-testid="agent-avatar-input" />
                    <button type="button" onClick={() => avatarFileRef.current?.click()} disabled={avatarUploading}
                        data-testid="agent-avatar-btn"
                        className="w-16 h-16 rounded-full border border-border overflow-hidden bg-[#0a0a0a] flex items-center justify-center hover:border-primary/50 transition-colors group"
                        aria-label="Foto do agente">
                        {avatarUploading ? (
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        ) : avatar.url ? (
                            <img src={avatar.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <Bot className="w-7 h-7 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.8} />
                        )}
                    </button>
                    <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center border-2 border-[#0d0d0d] pointer-events-none">
                        <Upload className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                </div>
                <div className="min-w-0">
                    <div className="text-xs font-semibold">Foto do agente</div>
                    {avatar.url && (
                        <button type="button" onClick={() => setAvatar({ url: "", key: "" })} data-testid="agent-avatar-remove"
                            className="mt-1 text-[10px] text-red-400 hover:text-red-300 transition-colors">Remover foto</button>
                    )}
                </div>
            </div>

            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Nome do agente</label>
                <input
                    ref={nameRef}
                    defaultValue={agent?.name || ""}
                    maxLength={80}
                    placeholder="Ex.: Redator Publicitário, Dev Sênior, Consultor Financeiro..."
                    data-testid="agent-name-input"
                    className="mt-1.5 w-full bg-[#0a0a0a] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary/60 transition-colors placeholder:text-muted-foreground/50"
                />
            </div>

            <div>
                <div className="flex items-center justify-between">
                    <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Descrição detalhada — sem limite</label>
                    {loadingDesc && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </div>
                <textarea
                    ref={descRef}
                    defaultValue={agent?.description || ""}
                    onInput={updateCounter}
                    rows={8}
                    placeholder="Descreva TUDO sobre esse agente: comportamento, tom de voz, especialidades, regras, exemplos, instruções completas... Sem limite de tamanho."
                    data-testid="agent-description-input"
                    className="mt-1.5 w-full bg-[#0a0a0a] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary/60 transition-colors resize-y min-h-[120px] placeholder:text-muted-foreground/50"
                />
                <div className="flex items-center justify-end mt-1">
                    <span ref={counterRef} className="text-[10px] font-mono text-muted-foreground">
                        {(agent?.description?.length || 0).toLocaleString("pt-BR")} caracteres
                    </span>
                </div>
            </div>

            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Habilidades e comandos</label>
                <div className="mt-1.5 flex gap-2 items-start">
                    <textarea
                        ref={skillRef}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addSkill(); } }}
                        maxLength={2000}
                        rows={2}
                        placeholder="Adicione habilidades, comandos ou instruções... (Enter para adicionar, Shift+Enter para nova linha)"
                        data-testid="agent-skill-input"
                        className="flex-1 bg-[#0a0a0a] border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary/60 transition-colors resize-y placeholder:text-muted-foreground/50"
                    />
                    <button type="button" onClick={addSkill} data-testid="agent-skill-add-btn"
                        className="px-3 py-2.5 rounded-md border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors shrink-0" aria-label="Adicionar habilidade">
                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                </div>
                {skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="agent-skills-list">
                        {skills.map((s, i) => (
                            <span key={i} className="flex items-center gap-1 text-xs font-mono bg-primary/10 border border-primary/30 text-primary rounded-full pl-2.5 pr-1 py-0.5 max-w-full">
                                <span className="truncate max-w-[280px]">{s}</span>
                                <button type="button" onClick={() => setSkills((prev) => prev.filter((_, idx) => idx !== i))}
                                    className="p-0.5 hover:bg-primary/20 rounded-full shrink-0" aria-label={`Remover ${s}`} data-testid={`agent-skill-remove-${i}`}>
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Base de conhecimento — arquivos</label>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                    Anexe imagens, documentos, planilhas... A IA consulta esses arquivos em todas as conversas do agente.
                </p>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFiles} data-testid="agent-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="agent-attach-btn"
                    className="mt-2 w-full py-2.5 rounded-md border border-dashed border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Enviando arquivos..." : "Anexar arquivos (qualquer tipo)"}
                </button>
                {knowledgeFiles.length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid="agent-files-list">
                        {knowledgeFiles.map((f, i) => (
                            <div key={f.key || i} className="flex items-center gap-2 border border-border bg-[#0a0a0a] rounded-md px-3 py-2">
                                <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                                <a href={f.url} target="_blank" rel="noreferrer" className="text-xs truncate flex-1 hover:text-primary transition-colors">{f.filename}</a>
                                <span className="text-[10px] font-mono text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
                                <button type="button" onClick={() => setKnowledgeFiles((prev) => prev.filter((_, idx) => idx !== i))}
                                    data-testid={`agent-file-remove-${i}`}
                                    className="p-1 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0" aria-label={`Remover ${f.filename}`}>
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex gap-2 pt-1">
                <button type="button" onClick={onCancel}
                    className="flex-1 py-2.5 rounded-full border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    Cancelar
                </button>
                <button type="submit" disabled={saving || uploading} data-testid="agent-save-btn"
                    className="flex-1 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" strokeWidth={2.2} />}
                    {agent ? "Salvar alterações" : "Criar agente"}
                </button>
            </div>
        </form>
    );
}
